import {
  box,
  FormGroupState,
  KeyValue,
  ResetAction,
  SetValueAction,
} from 'ngrx-forms';
import { map, Observable, withLatestFrom } from 'rxjs';

import { ColumnState, IServerSideGetRowsParams } from '@ag-grid-community/core';
import {
  AgGridFilterModel,
  FilterFormValueInterface,
  GridState,
  LazyEntityFacadeInterface,
  RouterFacadeInterface,
  SharedEntityFacadeInterface,
} from '@ideapa/app-common';
import { BaseDto } from '@ideapa/common-dto';
import { FieldConfig, FilterValue } from '@ideapa/common-interfaces';
import { select, Store } from '@ngrx/store';

import { LazyEntityActions, LazyEntitySelectors } from './models';

export abstract class BaseLazyEntityFacade<
  T extends BaseDto,
  V,
  K extends KeyValue
> implements LazyEntityFacadeInterface<T, K>
{
  form$ = this.store.pipe(select(this.selectors.selectEntityForm));
  entityId$ = this.store.pipe(select(this.selectors.selectEntityId));
  entity$ = this.store.pipe(select(this.selectors.getSelectedEntity));
  filterForm$ = this.store.pipe(select(this.selectors.selectEntityFilterForm));
  abstract getBreadcrumb$: Observable<any[]>;

  getGridState$ = this.store.pipe(select(this.selectors.getGridState));
  getPageSize$ = this.store.pipe(select(this.selectors.getPageSize));
  getCurrentPage$ = this.store.pipe(select(this.selectors.getCurrentPage));
  getTotalRows$ = this.store.pipe(select(this.selectors.getTotalRows));
  pageLoaded$ = this.store.pipe(select(this.selectors.pageLoaded));
  getColumnState$ = this.store.pipe(select(this.selectors.getColumnState));
  getFilterModel$ = this.store.pipe(select(this.selectors.getFilterModel));
  getLoadedEntity$ = this.store.pipe(select(this.selectors.getLoadedEntity));
  showAdvancedFilters$ = this.store.pipe(
    select(this.selectors.showAdvancedFilters)
  );
  queryParams$ = this.store.pipe(select(this.selectors.getQueryParams));
  isNew$ = this.store.pipe(select(this.selectors.isNew));
  canSave$ = this.isNew$.pipe(
    withLatestFrom(this.sharedFacade.canCreate$, this.sharedFacade.canUpdate$),
    map(([isNew, canCreate, canUpdate]) => {
      return isNew ? canCreate : canUpdate;
    })
  );
  canDelete$ = this.isNew$.pipe(
    withLatestFrom(this.sharedFacade.canDelete$),
    map(([isNew, canDelete]) => canDelete && !isNew)
  );
  canAdd$: Observable<boolean> = this.sharedFacade.canCreate$;
  canEdit$: Observable<boolean> = this.sharedFacade.canUpdate$;

  constructor(
    protected store: Store<V>,
    protected selectors: LazyEntitySelectors<T, V, K>,
    protected actions: LazyEntityActions<T, K>,
    protected entityFormId: string,
    protected entityFilterFormId: string,
    protected sharedFacade: SharedEntityFacadeInterface<T>,
    protected routerFacade: RouterFacadeInterface
  ) {}

  abstract formInitialize(
    item: T | undefined,
    disable?: boolean,
    options?: any,
    permissions?: string[]
  ): void;

  setFilterFormValue(value: FilterValue[]) {
    this.store.dispatch(
      new SetValueAction<FilterFormValueInterface>(this.entityFilterFormId, {
        filters: box(value),
      })
    );
  }

  formSave(form: FormGroupState<K>, callback?: (item: T) => void) {
    this.store.dispatch(this.actions.formSave({ form: form, callback }));
  }

  displayFormError() {
    this.store.dispatch(this.actions.displayFormError());
  }

  resetForm() {
    this.store.dispatch(new ResetAction(this.entityFormId));
  }

  formDelete() {
    this.store.dispatch(this.actions.formDelete());
  }

  selectEntityId(id: number) {
    this.store.dispatch(this.actions.selectEntityId({ id }));
  }

  addEntity() {
    this.store.dispatch(this.actions.addEntity());
  }

  selectItem(item: T) {
    this.store.dispatch(this.actions.selectItem({ item }));
  }

  clearEntityId() {
    this.store.dispatch(this.actions.selectEntityId({ id: undefined }));
  }

  loadGridPage(params: IServerSideGetRowsParams) {
    this.store.dispatch(this.actions.loadGridPage({ params }));
  }

  loadGridPageSuccess(rows: T[]) {
    this.store.dispatch(this.actions.loadGridPageSuccess({ rows }));
  }

  setGridState(gridState: GridState) {
    this.store.dispatch(this.actions.setGridState({ gridState }));
  }

  setCurrentPage(currentPage: number) {
    this.store.dispatch(this.actions.setCurrentPage({ currentPage }));
  }

  setPageLoaded(loaded: boolean) {
    this.store.dispatch(this.actions.setPageLoaded({ loaded }));
  }

  setPageSize(pageSize: number) {
    this.store.dispatch(this.actions.setPageSize({ pageSize }));
  }

  setTotalRows(totalRows: number) {
    this.store.dispatch(this.actions.setTotalRows({ totalRows }));
  }

  setFilterModel(filterModel?: AgGridFilterModel) {
    this.store.dispatch(this.actions.setFilterModel({ filterModel }));
  }

  setColumnState(columnState?: ColumnState[]) {
    this.store.dispatch(this.actions.setColumnState({ columnState }));
  }

  downloadExcel(
    fileName?: string,
    columns?: FieldConfig[],
    join?: string[],
    filters?: FilterValue[],
    orderBy?: { [field: string]: 'asc' | 'desc' }
  ) {
    this.store.dispatch(
      this.actions.downloadExcel({
        fileName,
        columns,
        join,
        filters,
        orderBy,
      })
    );
  }

  showAdvancedFilters(show: boolean) {
    this.store.dispatch(this.actions.showAdvancedFilters({ show }));
  }
}
